import { Controller } from "@hotwired/stimulus"

// Cards for reference chips (ReferenceChipHelper). One controller on the page
// body listens for every chip, so chips in rows that Turbo morphs in later
// need nothing of their own.
//
// With a mouse, a card is a hover card: after a short pause on the chip it
// loads and shows, so skimming across a paragraph neither flashes cards nor
// sends a request for every chip it passes. It sits under the chip, over it
// when there is no room below, beside it when it fits neither way, and never
// past the window's edge (cardPlacement). It stays while the pointer is on the
// chip or the card. Keyboard focus opens
// it too. Escape, a scroll, a click or a page change closes it.
//
// On a touch screen there is no hover, so a tap on a chip opens the card as a
// bottom sheet instead: full width, with room for a thumb on its actions. It
// shows at once with the chip's own label while the card loads, and closes on
// a swipe down, a tap on the backdrop, or Escape. If the card cannot load, the
// tap opens the record the way a link would.
//
// Any card's "Copy" button puts the reference on the clipboard.
//
// Other links can carry a card too (the details panel's file tiles): they get
// the hover card, but a tap on a touch screen just follows the link, because
// the tile already shows what the card would and the tap means "open it".
//
// Usage: <body data-controller="ref-card">

const OPEN_DELAY = 150
const CLOSE_DELAY = 150
const EDGE = 8
const GAP = 6
const SWIPE_CLOSE = 80
const CHIP = "a[data-ref-card]"
// A chip with nothing to open but its card (a teammate): a click opens the card.
const CARD_ONLY_CHIP = "a[data-ref-card-only]"
const SHEET_CHIP = `a.ref-chip[data-ref-card], a.person-chip[data-ref-card], ${CARD_ONLY_CHIP}`

// Where a hover card goes, in viewport pixels: under the chip, else over it,
// else beside it (right, then left) and slid up or down to stay in the
// window. A card taller than the window keeps the larger room above or below
// and scrolls inside it. Never past an edge.
export function cardPlacement(anchor, card, view) {
  const clampLeft = left => Math.min(Math.max(EDGE, left), view.width - card.width - EDGE)
  const clampTop = top => Math.min(Math.max(EDGE, top), view.height - card.height - EDGE)
  const below = view.height - EDGE - (anchor.bottom + GAP)
  const above = anchor.top - GAP - EDGE

  if (card.height <= below) return { side: "bottom", top: anchor.bottom + GAP, left: clampLeft(anchor.left) }
  if (card.height <= above) return { side: "top", top: anchor.top - GAP - card.height, left: clampLeft(anchor.left) }

  if (card.height <= view.height - EDGE * 2) {
    const right = anchor.right + GAP * 2
    if (right + card.width <= view.width - EDGE) return { side: "right", top: clampTop(anchor.top - 12), left: right }
    const left = anchor.left - GAP * 2 - card.width
    if (left >= EDGE) return { side: "left", top: clampTop(anchor.top - 12), left }
  }

  if (below >= above) return { side: "bottom", top: anchor.bottom + GAP, left: clampLeft(anchor.left), maxHeight: below }
  return { side: "top", top: EDGE, left: clampLeft(anchor.left), maxHeight: above }
}

export default class extends Controller {
  connect() {
    for (const name of ["over", "out", "focusIn", "focusOut", "keydown", "click", "pointerDown", "hide", "hideFlyover"]) {
      this[name] = this[name].bind(this)
    }

    this.element.addEventListener("pointerover", this.over)
    this.element.addEventListener("pointerout", this.out)
    this.element.addEventListener("pointerdown", this.pointerDown, true)
    this.element.addEventListener("focusin", this.focusIn)
    this.element.addEventListener("focusout", this.focusOut)
    this.element.addEventListener("click", this.click)
    document.addEventListener("keydown", this.keydown)
    document.addEventListener("scroll", this.hideFlyover, true)
    window.addEventListener("resize", this.hideFlyover)
    document.addEventListener("turbo:before-visit", this.hide)
    document.addEventListener("turbo:before-cache", this.hide)

    this.nameFrame = null
    this.nameObserver = new MutationObserver(() => this.scheduleNames())
    this.nameObserver.observe(this.element, { childList: true, subtree: true })
    this.scheduleNames()
  }

  disconnect() {
    this.nameObserver?.disconnect()
    if (this.nameFrame) cancelAnimationFrame(this.nameFrame)
    this.element.removeEventListener("pointerover", this.over)
    this.element.removeEventListener("pointerout", this.out)
    this.element.removeEventListener("pointerdown", this.pointerDown, true)
    this.element.removeEventListener("focusin", this.focusIn)
    this.element.removeEventListener("focusout", this.focusOut)
    this.element.removeEventListener("click", this.click)
    document.removeEventListener("keydown", this.keydown)
    document.removeEventListener("scroll", this.hideFlyover, true)
    window.removeEventListener("resize", this.hideFlyover)
    document.removeEventListener("turbo:before-visit", this.hide)
    document.removeEventListener("turbo:before-cache", this.hide)
    this.hide()
  }

  // ── Which kind of pointer ────────────────────────────────────────────────

  pointerDown(event) {
    this.lastPointerType = event.pointerType
  }

  touching() {
    return this.lastPointerType === "touch" || window.matchMedia("(hover: none)").matches
  }

  // ── Hover card (mouse and keyboard) ──────────────────────────────────────

  over(event) {
    if (event.pointerType === "touch" || this.sheet) return
    if (this.pop?.contains(event.target)) return this.keepOpen()

    const chip = event.target.closest?.(CHIP)
    if (!chip) return
    this.quietTitle(chip)
    if (chip === this.chip) return this.keepOpen()
    this.schedule(chip)
  }

  // A chip that opens a card has no use for the browser's own tooltip, which
  // would sit over the card saying less. Its words move to a data attribute
  // (the sheet's label still reads them) and stay in the accessible name.
  quietTitle(chip) {
    const title = chip.getAttribute("title")
    if (!title) return
    chip.dataset.refTitle = title
    if (!chip.hasAttribute("aria-label")) chip.setAttribute("aria-label", title)
    chip.removeAttribute("title")
  }

  chipLabel(chip) {
    return chip.dataset.refTitle || chip.getAttribute("title") || chip.textContent.trim()
  }

  out(event) {
    if (this.sheet) return
    const to = event.relatedTarget
    const from = event.target.closest?.(CHIP) || (this.pop?.contains(event.target) && this.pop)
    if (!from) return
    if (to && (from.contains(to) || this.pop?.contains(to) || this.chip?.contains(to))) return

    if (from === this.pending && !this.pop) this.cancel()
    else this.scheduleClose()
  }

  focusIn(event) {
    if (this.sheet || this.touching()) return
    const chip = event.target.closest?.(CHIP)
    if (chip && chip !== this.chip) this.schedule(chip)
  }

  focusOut(event) {
    if (this.sheet) return
    const to = event.relatedTarget
    if (to && this.pop?.contains(to)) return
    if (event.target.closest?.(CHIP)) this.scheduleClose()
  }

  schedule(chip) {
    // Moving straight from one chip to the next: the card just left is on its
    // way out, and its close must not cancel this one's open. The old card
    // stays until the new one replaces it.
    clearTimeout(this.closeTimer)
    this.cancel()
    this.pending = chip
    // The request starts only once the pointer has stayed: a chip passed on
    // the way elsewhere costs nothing, and leaving the chip before the pause
    // ends cancels the timer before any request exists.
    this.openTimer = setTimeout(async () => {
      const request = this.load(chip.dataset.refCard)
      this.pendingRequest = request
      const html = await request
      if (this.pending !== chip || this.pendingRequest !== request || !html || !chip.isConnected) return
      this.showFlyover(chip, html)
    }, OPEN_DELAY)
  }

  cancel() {
    clearTimeout(this.openTimer)
    this.pending = null
    this.pendingRequest = null
  }

  keepOpen() {
    clearTimeout(this.closeTimer)
  }

  scheduleClose() {
    clearTimeout(this.closeTimer)
    this.closeTimer = setTimeout(this.hideFlyover, CLOSE_DELAY)
  }

  showFlyover(chip, html) {
    this.hide()
    const pop = document.createElement("div")
    pop.className = "ref-card-pop"
    pop.id = "ref-card-pop"
    pop.setAttribute("role", "tooltip")
    pop.innerHTML = html
    pop.style.visibility = "hidden"
    // A modal dialog sits in the top layer, above every z-index: a card for a
    // chip inside one must live in it, or it opens behind the dialog.
    this.hostFor(chip).appendChild(pop)
    this.place(chip, pop)
    pop.style.visibility = ""

    chip.setAttribute("aria-describedby", pop.id)
    this.chip = chip
    this.pop = pop
    this.pending = null
  }

  hostFor(chip) {
    return chip.closest("dialog[open]") || document.body
  }

  place(chip, pop) {
    const anchor = chip.getBoundingClientRect()
    const { width, height } = pop.getBoundingClientRect()

    // A file in the details panel opens its card beside the panel, not under
    // the file, so it never covers the next file the pointer is heading to.
    const shelf = chip.closest(".file-shelf")?.getBoundingClientRect()
    if (shelf && shelf.left - GAP - width > EDGE) {
      const sideTop = Math.min(Math.max(EDGE, anchor.top - 12), window.innerHeight - height - EDGE)
      pop.dataset.side = "left"
      pop.style.top = `${Math.round(sideTop)}px`
      pop.style.left = `${Math.round(shelf.left - GAP * 2 - width)}px`
      return
    }

    const placement = cardPlacement(anchor, { width, height }, { width: window.innerWidth, height: window.innerHeight })
    if (placement.side !== "bottom") pop.dataset.side = placement.side
    if (placement.maxHeight) {
      pop.style.maxHeight = `${Math.round(placement.maxHeight)}px`
      pop.style.overflowY = "auto"
    }
    pop.style.top = `${Math.round(placement.top)}px`
    pop.style.left = `${Math.round(placement.left)}px`
  }

  hideFlyover() {
    if (this.sheet) return
    this.cancel()
    clearTimeout(this.closeTimer)
    this.chip?.removeAttribute("aria-describedby")
    this.pop?.remove()
    this.chip = null
    this.pop = null
  }

  // ── Bottom sheet (touch) ─────────────────────────────────────────────────

  click(event) {
    const copy = event.target.closest?.("[data-ref-card-copy]")
    if (copy) {
      event.preventDefault()
      this.copy(copy)
      return
    }

    const cardOnly = event.target.closest?.(CARD_ONLY_CHIP)
    if (cardOnly && !this.touching()) {
      event.preventDefault()
      if (cardOnly !== this.chip) this.schedule(cardOnly)
      return
    }

    const chip = event.target.closest?.(SHEET_CHIP)
    if (chip && !this.passThrough && this.touching()) {
      // Turbo skips a click whose default is prevented; propagation is left
      // alone so nothing else that listens on the document misses it.
      event.preventDefault()
      this.openSheet(chip)
      return
    }
    this.passThrough = false

    if (!(this.pop || this.sheet)) return

    // A link or a form button in a card (Open …, Mark central) acts, and the
    // card goes once the click has run its course. Removed now, the link would be detached before
    // Turbo's document listener saw the click, and the browser would follow
    // it as a whole page — to a frame-only URL that dead-ends.
    if (event.target.closest?.(".ref-card a, .ref-card form button")) {
      setTimeout(this.hide, 0)
    } else if (event.target.closest?.(".ref-card-backdrop") || !event.target.closest?.(".ref-card-pop, .ref-card-sheet")) {
      this.hide()
    }
  }

  async openSheet(chip) {
    this.hide()
    const backdrop = document.createElement("div")
    backdrop.className = "ref-card-backdrop"
    const sheet = document.createElement("div")
    sheet.className = "ref-card-sheet"
    sheet.setAttribute("role", "dialog")
    sheet.setAttribute("aria-modal", "true")
    sheet.setAttribute("aria-label", this.chipLabel(chip))
    sheet.tabIndex = -1
    sheet.innerHTML = `<span class="ref-card-sheet__handle" aria-hidden="true"></span>
      <div class="ref-card-sheet__body"><div class="ref-card ref-card--loading">
        <p class="ref-card__title"></p><p class="ref-card__loading">Loading…</p></div></div>`
    sheet.querySelector(".ref-card__title").textContent = this.chipLabel(chip)
    this.hostFor(chip).append(backdrop, sheet)
    document.documentElement.classList.add("ref-card-sheet-open")
    this.sheet = sheet
    this.backdrop = backdrop
    this.chip = chip
    this.bindSwipe(sheet)
    sheet.focus({ preventScroll: true })

    const html = await this.load(chip.dataset.refCard)
    if (this.sheet !== sheet) return
    if (!html) {
      // No card for this viewer: behave as the link it is. A chip that is
      // only a card (a teammate) has no link to follow, so it just closes.
      this.hide()
      if (chip.matches(CARD_ONLY_CHIP)) return
      this.passThrough = true
      chip.click()
      return
    }
    sheet.querySelector(".ref-card-sheet__body").innerHTML = html
  }

  bindSwipe(sheet) {
    let startY = null
    let dy = 0
    sheet.addEventListener("touchstart", (event) => {
      if (sheet.querySelector(".ref-card-sheet__body").scrollTop > 0) return
      startY = event.touches[0].clientY
      dy = 0
      sheet.style.transition = "none"
    }, { passive: true })
    sheet.addEventListener("touchmove", (event) => {
      if (startY === null) return
      dy = Math.max(0, event.touches[0].clientY - startY)
      sheet.style.translate = `0 ${dy}px`
    }, { passive: true })
    sheet.addEventListener("touchend", () => {
      if (startY === null) return
      startY = null
      sheet.style.transition = ""
      if (dy > SWIPE_CLOSE) this.hide()
      else sheet.style.translate = ""
    })
  }

  // ── Shared ───────────────────────────────────────────────────────────────

  keydown(event) {
    if (event.key !== "Escape" || !(this.pop || this.sheet)) return
    const chip = this.chip
    this.hide()
    chip?.focus({ preventScroll: true })
  }

  async copy(button) {
    const label = button.querySelector("[data-ref-card-copy-label]")
    try {
      await navigator.clipboard.writeText(button.dataset.refCardCopy)
      if (label) {
        const before = label.textContent
        label.textContent = "Copied"
        setTimeout(() => { label.textContent = before }, 1500)
      }
    } catch {
      // No clipboard access (an insecure origin, a denied permission): the
      // reference is on the card to read, so nothing else is needed.
    }
  }

  // ── Names for chips that cannot carry one ────────────────────────────────
  //
  // A contact's chip is rendered with only its reference: the markup is shared,
  // and the server cannot tell who will read it. The card is checked against
  // the viewer, so a viewer who may open it gets the name written over the
  // reference; anyone else keeps the reference, exactly as before.

  scheduleNames() {
    if (this.nameFrame) return
    this.nameFrame = requestAnimationFrame(() => {
      this.nameFrame = null
      this.resolveNames()
    })
  }

  resolveNames() {
    const pending = new Map()
    for (const chip of this.element.querySelectorAll("a[data-ref-resolve='name']:not([data-ref-resolved])")) {
      const url = chip.dataset.refCard
      if (!url) continue
      chip.dataset.refResolved = "pending"
      if (!pending.has(url)) pending.set(url, this.load(url).then((html) => this.cardTitle(html)))
      pending.get(url).then((name) => this.nameChip(chip, name))
    }
  }

  cardTitle(html) {
    if (!html) return null
    const doc = new DOMParser().parseFromString(html, "text/html")
    return doc.querySelector(".ref-card__title-text")?.textContent.trim() || null
  }

  nameChip(chip, name) {
    const label = chip.querySelector(".ref-chip__label")
    if (!name || !label || !chip.isConnected) {
      chip.dataset.refResolved = "failed"
      return
    }
    const reference = label.textContent.trim()
    label.textContent = name
    for (const attribute of ["title", "data-ref-title", "aria-label"]) {
      if (chip.hasAttribute(attribute)) chip.setAttribute(attribute, `${name} · ${reference}`)
    }
    chip.dataset.refResolved = "done"
  }

  load(url) {
    // Anything but a card — a refusal, or a redirect to sign-in for a viewer
    // on a share link — means no card, and the chip stays a plain link.
    // Reauthorize every opening: the acting user or their roles can change
    // while Turbo keeps this module alive, even when the URL is unchanged.
    return fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin", redirect: "error", cache: "no-store" })
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => (text && text.includes('class="ref-card"') ? text : null))
      .catch(() => null)
  }

  hide() {
    this.cancel()
    clearTimeout(this.closeTimer)
    this.chip?.removeAttribute("aria-describedby")
    this.pop?.remove()
    this.sheet?.remove()
    this.backdrop?.remove()
    document.documentElement.classList.remove("ref-card-sheet-open")
    this.chip = null
    this.pop = null
    this.sheet = null
    this.backdrop = null
  }
}
